export type WalletDatePreset =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last14'
  | 'last30'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'
  | 'allTime'
  | 'custom'

export type WalletFilterState = {
  preset: WalletDatePreset
  date_from: string
  date_to: string
  transaction_type: string
  label: string
  all_time?: boolean
}

export const WALLET_DATE_PRESET_OPTIONS: {id: WalletDatePreset; label: string}[] = [
  {id: 'today', label: 'Today'},
  {id: 'yesterday', label: 'Yesterday'},
  {id: 'last7', label: 'Last 7 days'},
  {id: 'last14', label: 'Last 14 days'},
  {id: 'last30', label: 'Last 30 days'},
  {id: 'thisWeek', label: 'This week (Mon–today)'},
  {id: 'lastWeek', label: 'Last week (Mon–Sun)'},
  {id: 'thisMonth', label: 'This month'},
  {id: 'lastMonth', label: 'Last month'},
  {id: 'thisYear', label: 'This year'},
  {id: 'lastYear', label: 'Last year'},
  {id: 'allTime', label: 'All time'},
  {id: 'custom', label: 'Custom range'},
]

export const toDateInputValue = (d: Date): string => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    return new Date().toLocaleDateString('en-CA')
  }
  return d.toLocaleDateString('en-CA')
}

export const formatDisplayDate = (isoDate: string): string => {
  const d = new Date(isoDate + 'T12:00:00')
  return d.toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'})
}

/** Monday 00:00 of the week containing `ref` (ISO week). */
const startOfWeekMonday = (ref: Date): Date => {
  const d = new Date(ref)
  d.setHours(0, 0, 0, 0)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return d
}

export const getDefaultWalletFilter = (): WalletFilterState => {
  const range = getRangeForPreset('today')
  return {...range, transaction_type: 'all'}
}

export const getRangeForPreset = (
  preset: WalletDatePreset,
  customFrom?: string,
  customTo?: string
): Omit<WalletFilterState, 'transaction_type'> => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  let from = new Date(today)
  let to = new Date(today)
  let label = 'Today'
  let all_time = false

  switch (preset) {
    case 'today':
      break
    case 'yesterday':
      from.setDate(from.getDate() - 1)
      to = new Date(from)
      label = 'Yesterday'
      break
    case 'last7':
      from.setDate(from.getDate() - 6)
      label = 'Last 7 days'
      break
    case 'last14':
      from.setDate(from.getDate() - 13)
      label = 'Last 14 days'
      break
    case 'last30':
      from.setDate(from.getDate() - 29)
      label = 'Last 30 days'
      break
    case 'thisWeek':
      from = startOfWeekMonday(today)
      label = 'This week'
      break
    case 'lastWeek': {
      const thisWeekStart = startOfWeekMonday(today)
      from = new Date(thisWeekStart)
      from.setDate(from.getDate() - 7)
      to = new Date(thisWeekStart)
      to.setDate(to.getDate() - 1)
      label = 'Last week'
      break
    }
    case 'thisMonth':
      from = new Date(today.getFullYear(), today.getMonth(), 1)
      label = 'This month'
      break
    case 'lastMonth':
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      to = new Date(today.getFullYear(), today.getMonth(), 0)
      label = 'Last month'
      break
    case 'thisYear':
      from = new Date(today.getFullYear(), 0, 1)
      label = 'This year'
      break
    case 'lastYear':
      from = new Date(today.getFullYear() - 1, 0, 1)
      to = new Date(today.getFullYear() - 1, 11, 31)
      label = 'Last year'
      break
    case 'allTime':
      all_time = true
      label = 'All time'
      return {preset, date_from: '', date_to: '', label, all_time}
    case 'custom': {
      const f = customFrom || toDateInputValue(today)
      const t = customTo || toDateInputValue(today)
      from = new Date(f + 'T00:00:00')
      to = new Date(t + 'T00:00:00')
      label = 'Custom range'
      return {preset, date_from: f, date_to: t, label, all_time: false}
    }
  }

  return {
    preset,
    date_from: toDateInputValue(from),
    date_to: toDateInputValue(to),
    label,
    all_time,
  }
}

const TRANSACTION_LABELS: Record<string, string> = {
  all: 'All transaction types',
  driver_earning: 'Trip earnings',
  trip_expense: 'Trip expenses',
  credit_purchase: 'Credit purchase',
  passenger_payment: 'Passenger payment (combined)',
  redeem: 'Gift card claim',
  referral: 'Social referral bonus',
  welcome_credit: 'Welcome credit',
  other_debit: 'Other debit',
}

export const getTransactionTypeLabel = (value: string): string =>
  TRANSACTION_LABELS[value] || value

export const buildWalletFilterSummary = (
  filter: WalletFilterState,
  search: string,
  totalRecords?: number
): string => {
  const rangeText = filter.all_time
    ? 'all dates'
    : filter.date_from === filter.date_to
      ? formatDisplayDate(filter.date_from)
      : `${formatDisplayDate(filter.date_from)} – ${formatDisplayDate(filter.date_to)}`

  const parts = [filter.label, `(${rangeText})`, getTransactionTypeLabel(filter.transaction_type)]
  if (search.trim()) parts.push(`search: "${search.trim()}"`)
  if (totalRecords !== undefined) {
    parts.push(`${totalRecords} transaction${totalRecords === 1 ? '' : 's'}`)
  }
  return parts.join(' · ')
}
